import { QueryClient } from '@tanstack/react-query';
import { type PersistedClient, type Persister } from "@tanstack/react-query-persist-client";
import { get, set, del } from "idb-keyval";

/** This is the trailing persist window. `throttlePersistClient` writes one IDB clone per window. See `docs/performance.md`. */
export const PERSIST_THROTTLE_MS = 1000

/**
 * This wrapper throttles persist writes. The library subscribe notifies on every cacheable cache write. This function keeps the latest snapshot and writes that snapshot once after `delay`.
 * `persistClient` stores the snapshot in `pending` and resolves immediately. The timer is armed once per window. Later notifies replace `pending` and do not reset the clock.
 * `flush` runs one IDB `set` at a time. The stall is the structured clone of the 19k talent blob (hundreds of ms to ~1.4s). A later persist waits for that clone to finish. Otherwise the slower clone can finish last, and the next visit restores a stale catalog.
 * When the write finishes, `flush` writes whatever newer snapshot arrived in `pending` during the clone. Hiding the tab calls `flush` so the current snapshot is not left waiting on the timer.
 */
export function throttlePersistClient(write: (client: PersistedClient) => Promise<void>, delay: number) {
    let timeout = 0
    let pending: PersistedClient | null = null
    let isWriting = false
    const flush = () => {
        window.clearTimeout(timeout)
        timeout = 0
        // An in-flight clone already owns IDB. This flush keeps `pending` (already the newest snapshot) and writes it when that clone finishes.
        if (isWriting || !pending) {
            return
        }
        const client = pending
        pending = null
        isWriting = true
        void write(client).catch(() => undefined).finally(() => {
            isWriting = false
            // A notify or tab-hide during the clone left a newer snapshot in `pending`. This flush writes that snapshot next. It does not start a second overlapping `set`.
            if (pending) {
                flush()
            }
        })
    }
    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                flush()
            }
        })
    }
    return (client: PersistedClient) => {
        pending = client
        // A flush is already queued for this window, or a clone is still running. This call only replaces `pending`.
        if (!timeout && !isWriting) {
            timeout = window.setTimeout(flush, delay)
        }
        return Promise.resolve()
    }
}

function createIDBPersister(idbValidKey: IDBValidKey = "reactQuery") {
    const write = async (client: PersistedClient) => {
        await set(idbValidKey, client)
    }
    return {
        persistClient: throttlePersistClient(write, PERSIST_THROTTLE_MS),
        restoreClient: async () => {
            return await get<PersistedClient>(idbValidKey);
        },
        removeClient: async () => {
            await del(idbValidKey);
        },
    } as Persister;
}

export const persister = createIDBPersister();
export const buster = '1790354060809'; // Manually replace when making changes that require cache-busting - use Date.now() to generate

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // https://stackoverflow.com/questions/77815938/what-is-the-maximum-duration-i-can-set-to-gctime-cachetime-in-react-query
            // cacheTime: 1000 * 60 * 60 * 24 * 30, // 30 days // NOTE: 30 days is too long because of JS setTimeout limit
            cacheTime: Infinity,
            staleTime: 1000 * 60 * 1, // 1 minutes
            // refetchOnMount: 'always', // NOTE: this doesn't override disabled queries... when a query re-enables, it won't refetch unless it's stale
        },
    },
});
